import { useMemo, useState, useEffect } from 'react';
import { EmailEditor, DEFAULT_EMAIL_SNIPPET_GROUPS, DEFAULT_HOST_IMAGES, EmailMessageState } from 'peak-editor-components';
import { useEditorConfig } from '../../../contexts/EditorContext';

interface EmailTemplateTabProps {
  data: any;
  onChange: (key: string, value: any) => void;
}

const COMMON_PROPS = {
    variables: { user: { name: 'John Doe', email: 'john@example.com' } },
    variablesJson: JSON.stringify({ user: { name: 'John Doe', email: 'john@example.com' } }, null, 2),
    onVariablesChange: () => {},
    variableError: null,
    functions: [],
    onFunctionsChange: () => {}
};

export const EmailTemplateTab: React.FC<EmailTemplateTabProps> = ({ data, onChange }) => {
  const { services } = useEditorConfig();
  const [hostImages, setHostImages] = useState(DEFAULT_HOST_IMAGES);
  const [servers, setServers] = useState<any[]>([]);

  useEffect(() => {
      let cancelled = false;
      services.notifications?.listMailServers().then(items => {
        if (!cancelled) setServers(items);
      }).catch(error => {
        console.error('Failed to load mail servers for workflow editor.', error);
        if (!cancelled) setServers([]);
      });
      return () => { cancelled = true; };
  }, [services.notifications]);

  const connections = useMemo(() => servers.map(s => ({
      id: s.key,
      name: `${s.key} (${s.host})`,
      dialect: 'postgres' as const, // Placeholder required by DbConnection type
      connectionString: s.host
  })), [servers]);

  // Construct editor state directly from props (Controlled Component pattern)
  const defaultConnection = connections[0]?.id || '';
  const editorContent = {
      html: data.body || '',
      meta: {
        connectionId: data.connectionId || defaultConnection,
        to: data.to || '',
        from: data.fromEmail || '',
        subject: data.subject || '',
        cc: data.cc || '',
        bcc: data.bcc || '',
        replyTo: data.replyTo || ''
      }
  };

  const handleEditorChange = useMemo(() => (newContent: EmailMessageState) => {
    // Propagate changes directly to the parent
    onChange('body', newContent.html);
    onChange('isHtml', true);

    // Meta fields
    onChange('to', newContent.meta.to);
    onChange('fromEmail', newContent.meta.from); // Note: Workflow engine expects 'fromEmail'
    onChange('subject', newContent.meta.subject);
    onChange('cc', newContent.meta.cc);
    onChange('bcc', newContent.meta.bcc);
    onChange('replyTo', newContent.meta.replyTo);
    onChange('connectionId', newContent.meta.connectionId);
  }, [onChange]);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 relative min-h-[600px]">
        <EmailEditor
            content={editorContent}
            onChange={handleEditorChange}
            emailBlockGroups={DEFAULT_EMAIL_SNIPPET_GROUPS}
            hostImages={hostImages}
            onAddImage={(img: any) => setHostImages((prev: any) => [...prev, img])}
            onDeleteImage={(id: string) => setHostImages((prev: any) => prev.filter((i: any) => i.id !== id))}
            connections={connections}
            {...COMMON_PROPS}
        />
      </div>
    </div>
  );
};
